import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { GeometricBackground } from '../components/GeometricBackground';
import { KhatimStarLogo } from '../components/AalimLogo';
import { Calendar, Eye, ArrowRight, Tag } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

const CATEGORY_COLORS = {
    "fiqh": "bg-emerald-500/10 text-emerald-400",
    "quran": "bg-blue-500/10 text-blue-400",
    "hadith": "bg-amber-500/10 text-amber-400",
    "aqeedah": "bg-purple-500/10 text-purple-400",
    "lifestyle": "bg-rose-500/10 text-rose-400",
    "general": "bg-primary/10 text-primary",
};

export default function BlogListPage() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API_URL}/api/blog/posts?published_only=true`)
            .then(r => r.json())
            .then(data => { setPosts(data.posts || []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <GeometricBackground />

            {/* Header */}
            <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-1">
                        <KhatimStarLogo size={36} />
                        <span className="font-heading text-xl font-semibold text-foreground -ml-1">aalim<span className="text-secondary">.ai</span></span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</Link>
                        <Link to="/login">
                            <Button variant="outline" size="sm">Sign In</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-32 pb-12 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.h1 variants={fadeUp} custom={0} initial="hidden" animate="visible"
                        className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">
                        <span className="text-secondary">Blog</span>
                    </motion.h1>
                    <motion.p variants={fadeUp} custom={1} initial="hidden" animate="visible"
                        className="text-base text-muted-foreground max-w-xl mx-auto">
                        Articles on Islamic knowledge, fiqh, Quran studies, and spiritual growth.
                    </motion.p>
                </div>
            </section>

            {/* Posts Grid */}
            <section className="pb-20 px-6">
                <div className="max-w-5xl mx-auto">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[1,2,3,4].map(i => (
                                <Card key={i} className="animate-pulse">
                                    <CardContent className="p-6">
                                        <div className="h-4 bg-muted rounded w-1/4 mb-4" />
                                        <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                                        <div className="h-4 bg-muted rounded w-full mb-2" />
                                        <div className="h-4 bg-muted rounded w-2/3" />
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-20" data-testid="blog-empty">
                            <p className="text-muted-foreground text-lg mb-2">No blog posts yet.</p>
                            <p className="text-sm text-muted-foreground">Check back soon for articles on Islamic knowledge and guidance.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-grid">
                            {posts.map((post, i) => (
                                <motion.div key={post.id} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                    <Link to={`/blogs/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
                                        <Card className="h-full border-border hover:border-secondary/40 transition-all duration-300 hover:shadow-lg hover:shadow-secondary/5 group cursor-pointer">
                                            {post.featured_image && (
                                                <div className="h-48 overflow-hidden rounded-t-lg">
                                                    <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                </div>
                                            )}
                                            <CardContent className="p-6">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general}`}>
                                                        {post.category}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </span>
                                                </div>
                                                <h2 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-secondary transition-colors line-clamp-2">
                                                    {post.title}
                                                </h2>
                                                <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                                                    {post.excerpt}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                        <Eye className="w-3 h-3" /> {post.views || 0} views
                                                    </div>
                                                    <span className="text-secondary text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                                        Read <ArrowRight className="w-3 h-3" />
                                                    </span>
                                                </div>
                                                {post.tags?.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-border">
                                                        {post.tags.slice(0, 3).map(tag => (
                                                            <span key={tag} className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded flex items-center gap-1">
                                                                <Tag className="w-2.5 h-2.5" />{tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 border-t border-secondary/20">
                <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
                        <Link to="/blogs" className="hover:text-foreground transition-colors">Blog</Link>
                        <Link to="/terms" className="hover:text-foreground transition-colors">Terms & Privacy</Link>
                    </div>
                    <p className="text-sm text-muted-foreground">&copy; 2026 aalim.ai</p>
                </div>
            </footer>
        </div>
    );
}
